import { apiClient } from "../client";

/**
 * Astrologer → seeker product suggestions during a consultation. All three
 * endpoints and payload shapes are verified against the legacy portal's
 * AstrologerApiService + AstrologerAppointmentDetailsController (its only
 * existing consumer): suggestions are cart rows tagged with the booking, so
 * "remove" takes the cart row id, and the list read is a POST filtered by
 * astrologer + booking.
 */

/** Product record as nested on a suggestion cart row (loosely typed upstream). */
export interface SuggestedProductInfo {
  id?: number;
  name?: string | null;
  slug?: string | null;
  /** Decimal strings, e.g. "10000.00". */
  price?: string | number | null;
  product_price?: string | number | null;
  discount_rate?: string | number | null;
  final_price?: string | number | null;
  image?: string | null;
  image_url?: string | null;
  [key: string]: unknown;
}

/** One suggestion = one cart row. `id` is the cart_id used for removal. */
export interface SuggestedProductRow {
  id: number;
  booking_id?: number | string;
  product_id?: number;
  quantity?: number;
  product?: SuggestedProductInfo | null;
  [key: string]: unknown;
}

/** Upstream list shapes vary — items may sit under data/items/carts or be a bare list. */
export interface SuggestedProductsResponse {
  status?: boolean;
  message?: string;
  data?: SuggestedProductRow[] | { data?: SuggestedProductRow[]; [key: string]: unknown };
  items?: SuggestedProductRow[];
  carts?: SuggestedProductRow[];
  [key: string]: unknown;
}

export interface SuggestProductPayload {
  product_id: number;
  quantity?: number;
  astrologer_id: number | string;
  user_id: number | string;
  booking_id: number | string;
  variation_id?: number;
}

export interface RemoveSuggestedProductPayload {
  /** The suggestion row's own id (cart row), not the product id. */
  cart_id: number;
  astrologer_id: number | string;
  booking_id: number | string;
}

export interface SuggestedProductMutationResponse {
  status?: boolean;
  message?: string;
  [key: string]: unknown;
}

export const suggestedProducts = {
  /** Suggestions for one consultation. Upstream may return rows from other
   * bookings of the same astrologer — filter by booking_id client-side. */
  list: (payload: { astrologer_id: number | string; booking_id: number | string }) =>
    apiClient.post<SuggestedProductsResponse>("/astrologer-suggested-products", payload),

  /** Suggest a product to the booking's customer (adds a tagged cart row). */
  add: (payload: SuggestProductPayload) =>
    apiClient.post<SuggestedProductMutationResponse>("/astrologer/add-to-cart", {
      quantity: 1,
      ...payload,
    }),

  /** Withdraw a suggestion by its cart row id. */
  remove: (payload: RemoveSuggestedProductPayload) =>
    apiClient.post<SuggestedProductMutationResponse>(
      "/astrologer/remove-suggested-product",
      payload
    ),
};
